import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Bell, Share, Plus, Smartphone, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { registerServiceWorker, enablePush, isStandalone, isIOS, isPushSupported, pushPermission } from "@/lib/push";

// Installable-app + push-notification prompt. variant="banner" (marketing) or "inline" (compact).
export const InstallPWA = ({ variant = "banner" }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);
  const [perm, setPerm] = useState(pushPermission());

  useEffect(() => {
    registerServiceWorker();
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); toast.success("LocalLease installed on your device"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const doInstall = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setDeferred(null);
      return;
    }
    if (isIOS()) { setIosOpen(true); return; }
    toast.message("Open your browser menu and choose “Install app” / “Add to Home Screen”.");
  };

  const doEnablePush = async () => {
    try {
      await enablePush();
      setPerm("granted");
      toast.success("Notifications on — we'll ping you about rent, applications & leases.");
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    }
  };

  const canInstall = !installed;
  const canEnablePush = isPushSupported() && perm !== "granted" && (installed || !isIOS());

  // Nothing actionable to show (e.g., installed + notifications already on).
  if (!canInstall && !canEnablePush) return null;

  const IosModal = (
    <Dialog open={iosOpen} onOpenChange={setIosOpen}>
      <DialogContent className="bg-white rounded-2xl max-w-sm" data-testid="ios-install-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Smartphone className="h-5 w-5 text-forest" /> Install on iPhone</DialogTitle>
          <DialogDescription>Add LocalLease to your home screen to get an app icon and push notifications.</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 text-sm text-ink">
          <li className="flex items-start gap-2"><Share className="h-4 w-4 text-forest mt-0.5 shrink-0" /> Tap the <b>Share</b> button in Safari's toolbar.</li>
          <li className="flex items-start gap-2"><Plus className="h-4 w-4 text-forest mt-0.5 shrink-0" /> Choose <b>Add to Home Screen</b>.</li>
          <li className="flex items-start gap-2"><Check className="h-4 w-4 text-forest mt-0.5 shrink-0" /> Open LocalLease from your home screen, then turn on notifications.</li>
        </ol>
      </DialogContent>
    </Dialog>
  );

  if (variant === "inline") {
    return (
      <div data-testid="install-pwa-inline" className="flex flex-wrap items-center gap-2">
        {canInstall && (
          <button data-testid="install-app-btn" onClick={doInstall} className="inline-flex items-center gap-1.5 text-sm border border-forest text-forest rounded-lg px-3 py-1.5 hover:bg-forest/5">
            <Download className="h-4 w-4" /> Install app
          </button>
        )}
        {canEnablePush && (
          <button data-testid="enable-push-btn" onClick={doEnablePush} className="inline-flex items-center gap-1.5 text-sm border border-line text-ink rounded-lg px-3 py-1.5 hover:bg-bone">
            <Bell className="h-4 w-4" /> Enable notifications
          </button>
        )}
        {IosModal}
      </div>
    );
  }

  return (
    <div data-testid="install-pwa-banner" className="bg-forest text-white rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><Smartphone className="h-6 w-6" /></div>
        <div>
          <p className="font-heading text-lg font-medium">Get the LocalLease app</p>
          <p className="text-sm text-white/80 mt-0.5">Install it on your phone for one-tap access and push reminders about rent, applications and leases.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {canInstall && (
          <button data-testid="install-app-btn" onClick={doInstall} className="inline-flex items-center gap-1.5 text-sm bg-white text-forest font-medium rounded-lg px-4 py-2 hover:bg-white/90">
            <Download className="h-4 w-4" /> Install app
          </button>
        )}
        {canEnablePush && (
          <button data-testid="enable-push-btn" onClick={doEnablePush} className="inline-flex items-center gap-1.5 text-sm border border-white/40 text-white rounded-lg px-4 py-2 hover:bg-white/10">
            <Bell className="h-4 w-4" /> Enable notifications
          </button>
        )}
      </div>
      {IosModal}
    </div>
  );
};
